/**
 * Groups a patient's standing summary points into a timeline (§11.14).
 *
 * Pure and Angular-free so `scripts/icd-summary-view-check.js` can exercise it. The grouping is
 * the whole readability of the screen, and it was wrong in three ways at once before:
 *
 *   - grouped by CATEGORY, so a resection and the findings from that same day sat in different
 *     sections with a scroll between them
 *   - the same date repeated on seven consecutive lines
 *   - unordered within a category (22 Jul, 22 Jul, 13 Jun, 13 Jun, 22 Jul, 5 May)
 *
 * A clinician opening this wants "what happened, most recent first", not an index.
 */

export interface SummaryPoint {
  text: string;
  category?: string;
  date?: string | null;
}

export interface TimelineDay {
  /** 'YYYY-MM-DD', or '' for the undated group. */
  key: string;
  label: string;
  points: SummaryPoint[];
}

/** Ordering WITHIN one date, so a day reads diagnosis → procedure → treatment → plan. */
export const CATEGORY_ORDER = ['diagnosis', 'procedure', 'treatment', 'risk', 'social', 'care-plan'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'YYYY-MM-DD' -> '22 Jul 2026'.
 *
 * ⚠ BUILT FROM THE STRING, never via `new Date()` or the `date` pipe. Angular parses a date-only
 * ISO string as UTC midnight and renders it in local time — correct at UTC+2, one day early at
 * any negative offset. Formatting from the parts removes the class of bug rather than relying on
 * every viewer sitting in SAST.
 */
export function formatDay(iso: string): string {
  const [year, month, day] = String(iso).split('-');
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName || !year || !day) return String(iso);
  return `${Number(day)} ${monthName} ${year}`;
}

function categoryRank(category?: string): number {
  const index = CATEGORY_ORDER.indexOf(String(category || ''));
  return index === -1 ? CATEGORY_ORDER.length : index;
}

/** Groups by date, newest first, undated last. */
export function buildTimeline(points: SummaryPoint[]): TimelineDay[] {
  const byDate = new Map<string, SummaryPoint[]>();

  for (const point of Array.isArray(points) ? points : []) {
    if (!point || !point.text) continue;
    // Undated points share one bucket sorted to the end. Shown rather than dropped — a summary
    // is more useful with them than without, and the pipeline legitimately emits a point with
    // no date when the timeline does not state one.
    const key = /^\d{4}-\d{2}-\d{2}$/.test(String(point.date || '')) ? String(point.date) : '';
    if (!byDate.has(key)) byDate.set(key, []);
    (byDate.get(key) as SummaryPoint[]).push(point);
  }

  const keys = [...byDate.keys()].sort((a, b) => {
    if (a === b) return 0;
    if (!a) return 1;        // undated last
    if (!b) return -1;
    return a < b ? 1 : -1;   // newest first
  });

  return keys.map((key) => ({
    key,
    label: key ? formatDay(key) : 'Date not recorded',
    points: (byDate.get(key) as SummaryPoint[]).slice()
      .sort((a, b) => categoryRank(a.category) - categoryRank(b.category)),
  }));
}

/**
 * A short label for one point's kind, shown inline rather than as a section heading.
 *
 * Diagnoses are deliberately unlabelled: they are most of a summary, and tagging every second
 * line makes the genuinely different entries — a procedure, a plan — harder to pick out.
 */
export function categoryLabel(category?: string): string {
  switch (category) {
    case 'procedure': return 'Procedure';
    case 'treatment': return 'Treatment';
    case 'risk': return 'Risk';
    case 'social': return 'Social';
    case 'care-plan': return 'Plan';
    default: return '';
  }
}
